import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Breadcrumbs, Link, TextField, InputAdornment, CircularProgress,
  Tabs, Tab
} from '@mui/material';
import { 
  Add as AddIcon, 
  Visibility as ViewIcon, 
  Search as SearchIcon,
  LocalShipping as ShippingIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import tripService from '../services/tripService';
import routeService from '../services/routeService';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useNotify } from '../contexts/FeedbackContext';
import Pagination from '../components/common/Pagination';

const getStatusColor = (status) => {
  switch (status) {
    case 'borrador': return { bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' };
    case 'en_curso': return { bg: '#e3f2fd', text: '#1976d2', border: '#90caf9' };
    case 'finalizado': return { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' };
    case 'cancelado': return { bg: '#ffebee', text: '#d32f2f', border: '#ef9a9a' };
    default: return { bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' };
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'borrador': return 'Borrador';
    case 'en_curso': return 'En Curso';
    case 'finalizado': return 'Finalizado';
    case 'cancelado': return 'Cancelado';
    default: return status;
  }
};

const TripsPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const notify = useNotify();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Weekly Navigation states
  const [referenceDate, setReferenceDate] = useState(new Date());

  // Tab & Routes states
  const [activeTab, setActiveTab] = useState(0);
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  // Helper: Get start (Monday) and end (Sunday) of the week for a given Date
  const getWeekRange = (date) => {
    const current = new Date(date);
    const day = current.getDay();
    // Monday is 1, Sunday is 0 in JS.
    const diff = current.getDate() - (day === 0 ? 6 : day - 1);
    const monday = new Date(current.setDate(diff));
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return { monday, sunday };
  };

  // Helper: Format date as YYYY-MM-DD
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper: Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  useEffect(() => {
    if (activeTab === 0) {
      const delayDebounce = setTimeout(() => {
        loadTrips(referenceDate, searchTerm, filterStatus);
      }, 300);
      return () => clearTimeout(delayDebounce);
    } else {
      loadRoutes();
    }
  }, [referenceDate, searchTerm, filterStatus, activeTab]);

  const loadRoutes = async () => {
    try {
      setLoadingRoutes(true);
      const data = await routeService.getRoutes();
      setRoutes(data);
    } catch (error) {
      console.error('Error fetching routes:', error);
      notify('No se pudieron cargar las rutas. Revisá la conexión e intentá de nuevo.', 'error');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const loadTrips = async (refDate = referenceDate, search = searchTerm, status = filterStatus) => {
    try {
      setLoading(true);
      const { monday, sunday } = getWeekRange(refDate);
      const params = {
        paginate: 'false',
        date_from: formatDateString(monday),
        date_to: formatDateString(sunday),
      };
      if (search) params.search = search;
      if (status !== 'all') params.status = status;

      const data = await tripService.getTrips(params);
      setTrips(data || []);
    } catch (error) {
      console.error('Error fetching trips:', error);
      notify('No se pudieron cargar los viajes. Revisá la conexión e intentá de nuevo.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredTrips = Array.isArray(trips) ? trips : [];

  // Group trips by date string
  const tripsByDate = {};
  filteredTrips.forEach(trip => {
    if (!tripsByDate[trip.date]) {
      tripsByDate[trip.date] = [];
    }
    tripsByDate[trip.date].push(trip);
  });

  const { monday, sunday } = getWeekRange(referenceDate);
  const weekDays = [];
  const weekdayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    weekDays.push({
      name: weekdayNames[i],
      date: day,
      formattedDate: formatDateString(day),
      displayDate: day.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    });
  }

  const statusFilters = [
    { id: 'all', label: 'Todos' },
    { id: 'borrador', label: 'Borrador' },
    { id: 'en_curso', label: 'En Curso' },
    { id: 'finalizado', label: 'Finalizado' },
    { id: 'cancelado', label: 'Cancelado' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            sx={{ color: '#4e7397', fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Inicio
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Viajes
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                color: '#0e141b', 
                fontSize: { xs: '1.875rem', md: '2.25rem' }, 
                fontWeight: 900, 
                mb: 1,
                letterSpacing: '-0.033em'
              }}
            >
              Viajes
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>
              Gestión de logística, seguimiento de entregas y planificación de rutas.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/trips/create')}
            sx={{
              bgcolor: '#800020',
              '&:hover': { bgcolor: '#600010' },
              fontWeight: 700,
              px: 3,
              py: 1.2,
            }}
          >
            Armar Nuevo Viaje
          </Button>
        </Box>
        {/* Tab Selector */}
        <Tabs 
          value={activeTab} 
          onChange={(e, val) => setActiveTab(val)} 
          sx={{ 
            mb: 4, 
            borderBottom: '1px solid #d0dbe7',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: '#4e7397',
              '&.Mui-selected': { color: '#800020' },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#800020',
            }
          }}
        >
          <Tab label="Historial de Reparto" icon={<ShippingIcon />} iconPosition="start" />
          <Tab label="Rutas de Reparto" icon={<RouteIcon />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 ? (
          <>
            {/* Filters & Search */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, mb: 4, alignItems: { xs: 'stretch', lg: 'center' }, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { height: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d0dbe7', borderRadius: '10px' } }}>
                {statusFilters.map((f) => (
                  <Button
                    key={f.id}
                    variant={filterStatus === f.id ? 'contained' : 'outlined'}
                    onClick={() => { setFilterStatus(f.id); }}
                    sx={{
                      bgcolor: filterStatus === f.id ? '#0e141b' : 'white',
                      color: filterStatus === f.id ? 'white' : '#4e7397',
                      borderColor: '#d0dbe7',
                      borderRadius: '9999px',
                      px: 2,
                      py: 0.75,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderColor: '#800020',
                        bgcolor: filterStatus === f.id ? '#0e141b' : '#f6f7f8',
                      }
                    }}
                  >
                    {f.label}
                  </Button>
                ))}
              </Box>
              <TextField
                placeholder="Buscar por ciudad o ID..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#4e7397', fontSize: '1.25rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: '100%', lg: '300px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                    backgroundColor: 'white',
                    '& fieldset': { borderColor: '#d0dbe7' },
                    '&:hover fieldset': { borderColor: '#800020' },
                    '&.Mui-focused fieldset': { borderColor: '#800020' },
                  },
                }}
              />
            </Box>

            {/* Week Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, bgcolor: 'white', p: 2, borderRadius: '0.75rem', border: '1px solid #d0dbe7', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    setReferenceDate(prev => {
                      const d = new Date(prev);
                      d.setDate(d.getDate() - 7);
                      return d;
                    });
                  }}
                  sx={{ borderColor: '#d0dbe7', color: '#0e141b', fontWeight: 700 }}
                >
                  Semana Anterior
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setReferenceDate(new Date())}
                  sx={{ borderColor: '#d0dbe7', color: '#0e141b', fontWeight: 700 }}
                >
                  Esta Semana
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    setReferenceDate(prev => {
                      const d = new Date(prev);
                      d.setDate(d.getDate() + 7);
                      return d;
                    });
                  }}
                  sx={{ borderColor: '#d0dbe7', color: '#0e141b', fontWeight: 700 }}
                >
                  Semana Siguiente
                </Button>
              </Box>
              <Typography sx={{ fontWeight: 800, color: '#0e141b', fontSize: '1rem' }}>
                Semana del {monday.toLocaleDateString('es-AR')} al {sunday.toLocaleDateString('es-AR')}
              </Typography>
            </Box>

            {/* Week Days List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {loading ? (
                <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CircularProgress size={30} sx={{ color: '#800020', mb: 1 }} />
                  <Typography sx={{ color: '#4e7397' }}>Cargando viajes...</Typography>
                </Box>
              ) : (
                weekDays.map((day) => {
                  const dayTrips = tripsByDate[day.formattedDate] || [];
                  const isDayToday = isToday(day.date);
                  
                  return (
                    <Paper 
                      key={day.formattedDate} 
                      sx={{ 
                        p: 3, 
                        borderRadius: '0.75rem', 
                        border: '1px solid #d0dbe7', 
                        borderColor: isDayToday ? '#800020' : '#d0dbe7',
                        borderLeft: isDayToday ? '6px solid #800020' : '1px solid #d0dbe7',
                        boxShadow: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: '1px solid #f6f7f8', pb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0e141b', fontSize: '1.1rem' }}>
                            {day.name}
                          </Typography>
                          <Typography sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.9rem' }}>
                            {day.displayDate}
                          </Typography>
                          {isDayToday && (
                            <Chip 
                              label="Hoy" 
                              size="small" 
                              sx={{ bgcolor: '#800020', color: 'white', fontWeight: 700, fontSize: '0.7rem', height: '20px' }} 
                            />
                          )}
                        </Box>
                        <Chip 
                          label={`${dayTrips.length} ${dayTrips.length === 1 ? 'viaje' : 'viajes'}`}
                          size="small" 
                          sx={{ bgcolor: '#f0f4f8', color: '#4e7397', fontWeight: 700, fontSize: '0.75rem' }} 
                        />
                      </Box>
                      
                      {dayTrips.length === 0 ? (
                        <Typography sx={{ color: '#90a4ae', fontSize: '0.875rem', fontStyle: 'italic', py: 1 }}>
                          Sin viajes programados
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {dayTrips.map((trip) => {
                            const statusStyle = getStatusColor(trip.status);
                            return (
                              <Box 
                                key={trip.id} 
                                sx={{ 
                                  display: 'flex', 
                                  flexDirection: { xs: 'column', md: 'row' },
                                  alignItems: { xs: 'flex-start', md: 'center' },
                                  justifyContent: 'space-between',
                                  p: 2, 
                                  bgcolor: '#f8fafc', 
                                  borderRadius: '0.5rem',
                                  border: '1px solid #e2e8f0',
                                  gap: 2,
                                  '&:hover': {
                                    borderColor: '#cbd5e1',
                                    bgcolor: '#f1f5f9'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                                  <Box>
                                    <Typography sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.875rem' }}>
                                      Viaje #{trip.id}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography sx={{ fontWeight: 700, color: '#0e141b' }}>
                                      {trip.city}
                                    </Typography>
                                    {trip.driver && (
                                      <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>
                                        Chofer: {trip.driver.first_name || trip.driver.username}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box>
                                    <Chip 
                                      label={`${trip.total_orders} pedidos`}
                                      size="small" 
                                      sx={{ bgcolor: '#e2e8f0', color: '#4e7397', fontWeight: 700, fontSize: '0.7rem' }} 
                                    />
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: { xs: 'stretch', md: 'auto' }, justifyContent: 'space-between' }}>
                                  <Chip 
                                    label={getStatusLabel(trip.status)} 
                                    size="small"
                                    sx={{ 
                                      bgcolor: statusStyle.bg, 
                                      color: statusStyle.text, 
                                      fontWeight: 700, 
                                      border: `1px solid ${statusStyle.border}`,
                                      fontSize: '0.7rem'
                                    }} 
                                  />
                                  <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => navigate(`/trips/${trip.id}`)}
                                    sx={{ borderColor: '#d0dbe7', color: '#0e141b', textTransform: 'none', fontWeight: 700, borderRadius: '0.5rem', bgcolor: 'white' }}
                                  >
                                    Ver Detalle
                                  </Button>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Paper>
                  );
                })
              )}
            </Box>
          </>
        ) : (
          /* Routes List View */
          <Box>
            {/* Desktop Routes Table */}
            <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none', overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                    <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', py: 2, width: '25%' }}>Nombre de la Ruta</TableCell>
                    <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Secuencia de Ciudades</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingRoutes ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={30} sx={{ color: '#800020', mb: 1 }} />
                        <Typography sx={{ color: '#4e7397' }}>Cargando rutas...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : routes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                        <RouteIcon sx={{ fontSize: 48, color: '#d0dbe7', mb: 2 }} />
                        <Typography sx={{ color: '#4e7397', fontWeight: 500 }}>No hay rutas de reparto configuradas.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    routes.map((route) => {
                      const sortedStops = [...route.route_cities].sort((a, b) => a.order - b.order);
                      return (
                        <TableRow key={route.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ py: 2.5 }}>
                            <Typography sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.875rem' }}>{route.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.875rem', color: '#4e7397', fontWeight: 500 }}>
                              {sortedStops.map(rc => rc.city_name).join(' → ') || 'Sin ciudades'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Mobile Routes Cards */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
              {loadingRoutes ? (
                <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress sx={{ color: '#800020' }} /></Box>
              ) : routes.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '1rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
                  <Typography sx={{ color: '#4e7397' }}>No hay rutas</Typography>
                </Paper>
              ) : (
                routes.map((route) => {
                  const sortedStops = [...route.route_cities].sort((a, b) => a.order - b.order);
                  return (
                    <Paper key={route.id} sx={{ p: 2, borderRadius: '1rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
                      <Typography sx={{ fontWeight: 800, color: '#0e141b', mb: 1 }}>{route.name}</Typography>
                      <Box sx={{ pt: 1, borderTop: '1px solid #f6f7f8' }}>
                        <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
                          {sortedStops.map(rc => rc.city_name).join(' → ') || 'Sin ciudades'}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TripsPage;
